import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MessageCircle, ChevronLeft, Send, Store, User as UserIcon, MoreVertical, Trash2, Paperclip, Image as ImageIcon, Video, X, AlertTriangle, Loader2, Flag } from "lucide-react";
import { toast } from "sonner";
import UserGem from "../components/UserGem";
import SwipeRow from "../components/SwipeRow";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    try {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        window.URL.revokeObjectURL(v.src);
        resolve(v.duration || 0);
      };
      v.onerror = () => reject(new Error("Couldn't read video metadata"));
      v.src = window.URL.createObjectURL(file);
    } catch (e) { reject(e); }
  });
}

function ConversationList({ conversations, activeId, onOpen, onAskDelete }) {
  return (
    <div className="divide-y divide-white/5" data-testid="conversations-list">
      {conversations.length === 0 && (
        <div className="px-4 py-16 text-center text-zinc-500" data-testid="no-conversations">
          <MessageCircle className="w-7 h-7 mx-auto mb-3 opacity-50" />
          <p>No messages yet.</p>
          <p className="text-xs mt-1">Open a vendor's storefront and tap "Message" to start a chat.</p>
        </div>
      )}
      {conversations.map((c) => {
        const active = c.conversation_id === activeId;
        return (
          <SwipeRow
            key={c.conversation_id}
            onDelete={() => onAskDelete(c)}
            testIdPrefix={`conv-${c.conversation_id}`}
          >
            <div
              className={`relative w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                active ? "bg-pink-500/10" : "hover:bg-white/[0.03]"
              }`}
            >
              <button
                onClick={() => onOpen(c)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
                data-testid={`conversation-${c.conversation_id}`}
              >
                <div className="w-11 h-11 rounded-full bg-pink-500/20 grid place-items-center overflow-hidden shrink-0 border border-pink-400/20">
                  {c.other_avatar ? (
                    <img src={fileUrl(c.other_avatar)} alt="" className="w-full h-full object-cover" />
                  ) : c.other_is_vendor ? (
                    <Store className="w-5 h-5 text-pink-200" />
                  ) : (
                    <UserIcon className="w-5 h-5 text-pink-200" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold truncate text-sm inline-flex items-center gap-1">
                      {c.other_is_vendor ? `@${c.other_name}` : c.other_name}
                      <UserGem userId={c.other_user_id} size="xs" />
                    </p>
                    <span className="text-[10px] text-zinc-500 shrink-0">{relTime(c.last_message_at)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-xs text-zinc-400 truncate">{c.last_message || "Say hi 👋"}</p>
                    {c.unread > 0 && (
                      <span className="min-w-5 h-5 px-1.5 rounded-full bg-pink-500 text-[10px] font-black text-white grid place-items-center" data-testid={`unread-${c.conversation_id}`}>
                        {c.unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAskDelete(c);
                }}
                aria-label="Conversation options"
                className="w-8 h-8 grid place-items-center rounded-full hover:bg-white/10 text-zinc-400 hover:text-white shrink-0"
                data-testid={`conversation-menu-${c.conversation_id}`}
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            </div>
          </SwipeRow>
        );
      })}
    </div>
  );
}

function Thread({ conversation, currentUserId, onBack, onAfterSend }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [deletingMsg, setDeletingMsg] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const [pendingAttachments, setPendingAttachments] = useState([]); // {attachment_id, kind, url, mime, size, name, uploading, error}
  const [reportTarget, setReportTarget] = useState(null);
  const [reporting, setReporting] = useState(false);

  const backendBase = process.env.REACT_APP_BACKEND_URL;
  const authUrl = (u) => {
    if (!u) return u;
    // /api/messages/attachments/{id} requires auth header — img/video tags can't
    // send Authorization. Append `?auth=` token query param that the backend
    // ALSO accepts (mirroring fileUrl's pattern for the public /api/files route).
    // For DMs we rely on the browser sending the JWT via cookie AND the
    // client-side interceptor uses Authorization. For the <img>/<video>
    // tags we fall back to auth token query.
    const token = localStorage.getItem("hg_token");
    const abs = u.startsWith("http") ? u : `${backendBase}${u}`;
    return token ? `${abs}${abs.includes("?") ? "&" : "?"}auth=${encodeURIComponent(token)}` : abs;
  };

  const load = useCallback(async () => {
    if (!conversation) return;
    try {
      const { data } = await api.get(`/messages/conversations/${conversation.conversation_id}/messages`);
      setMessages(data);
      await api.post(`/messages/conversations/${conversation.conversation_id}/read`);
    } catch { /* ignore */ }
  }, [conversation]);

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    const validAttachments = pendingAttachments.filter((a) => a.attachment_id && !a.uploading && !a.error);
    if (!t && validAttachments.length === 0) return;
    if (pendingAttachments.some((a) => a.uploading)) {
      toast.error("Wait for uploads to finish");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(
        `/messages/conversations/${conversation.conversation_id}/messages`,
        { text: t, attachment_ids: validAttachments.map((a) => a.attachment_id) }
      );
      setMessages((m) => [...m, data]);
      setText("");
      setPendingAttachments([]);
      onAfterSend?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send");
    } finally {
      setSending(false);
    }
  };

  const pickFiles = () => fileInputRef.current?.click();

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // 6-file soft cap
    const remainingSlots = Math.max(0, 6 - pendingAttachments.length);
    if (files.length > remainingSlots) {
      toast.error(`You can attach up to 6 items per message.`);
    }
    const take = files.slice(0, remainingSlots);
    e.target.value = "";
    for (const file of take) {
      const kind = (file.type || "").startsWith("video/") ? "video" : "image";
      const localId = `local_${Math.random().toString(36).slice(2, 9)}`;
      // Enforce client-side caps upfront so users get instant feedback
      const maxBytes = kind === "video" ? 40 * 1024 * 1024 : 8 * 1024 * 1024;
      if (file.size > maxBytes) {
        toast.error(`${file.name} exceeds ${kind === "video" ? "40" : "8"} MB.`);
        continue;
      }
      let durationHint = null;
      if (kind === "video") {
        try {
          durationHint = await getVideoDuration(file);
          if (durationHint > 30) {
            toast.error(`${file.name} is longer than 30s (DMs are 30s max).`);
            continue;
          }
        } catch { /* fall through — server also validates */ }
      }
      setPendingAttachments((prev) => [
        ...prev,
        { local_id: localId, kind, name: file.name, mime: file.type, size: file.size, uploading: true, error: null },
      ]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("conversation_id", conversation.conversation_id);
        fd.append("kind", kind);
        if (durationHint) fd.append("duration_hint_seconds", String(Math.ceil(durationHint)));
        const { data } = await api.post("/messages/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPendingAttachments((prev) => prev.map((a) =>
          a.local_id === localId
            ? { ...a, attachment_id: data.attachment_id, url: data.url, uploading: false }
            : a
        ));
      } catch (err) {
        setPendingAttachments((prev) => prev.map((a) =>
          a.local_id === localId
            ? { ...a, uploading: false, error: err.response?.data?.detail || "Upload failed" }
            : a
        ));
      }
    }
  };

  const removePending = (localId) => {
    setPendingAttachments((prev) => prev.filter((a) => a.local_id !== localId));
  };

  const reportMsg = async () => {
    if (!reportTarget) return;
    setReporting(true);
    try {
      await api.post(`/messages/${reportTarget.message_id}/report`, {
        reason: "other",
        detail: "Reported from message thread",
      });
      toast.success("Report sent to moderators.");
      setReportTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't report");
    } finally { setReporting(false); }
  };

  const confirmDeleteMsg = async () => {
    if (!deletingMsg) return;
    try {
      await api.delete(`/messages/${deletingMsg.message_id}`);
      setMessages((m) => m.filter((x) => x.message_id !== deletingMsg.message_id));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    } finally {
      setDeletingMsg(null);
    }
  };

  if (!conversation) {
    return (
      <div className="flex-1 grid place-items-center text-zinc-500 px-6 text-center">
        <div>
          <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Pick a chat to start messaging.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-zinc-950" data-testid="dm-thread">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 glass-strong">
        <button
          onClick={onBack}
          className="md:hidden w-9 h-9 grid place-items-center rounded-full bg-white/5 hover:bg-white/10"
          aria-label="Back"
          data-testid="dm-back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="w-9 h-9 rounded-full bg-pink-500/20 grid place-items-center overflow-hidden border border-pink-400/20">
          {conversation.other_avatar ? (
            <img src={fileUrl(conversation.other_avatar)} alt="" className="w-full h-full object-cover" />
          ) : conversation.other_is_vendor ? <Store className="w-4 h-4 text-pink-200" /> : <UserIcon className="w-4 h-4 text-pink-200" />}
        </div>
        <div>
          <p className="font-bold text-sm inline-flex items-center gap-1">
            {conversation.other_is_vendor ? `@${conversation.other_name}` : conversation.other_name}
            <UserGem userId={conversation.other_user_id} size="xs" />
          </p>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">{conversation.other_is_vendor ? "Vendor" : "Customer"}</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {messages.length === 0 && (
          <div className="text-center text-zinc-500 text-sm py-12">No messages yet. Say hi 👋</div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === currentUserId;
          return (
            <div
              key={m.message_id}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
              data-testid={`dm-msg-${m.message_id}`}
            >
              <div className="group relative max-w-[78%]">
                <div
                  className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                    mine
                      ? "bg-pink-500 text-white rounded-br-md"
                      : "bg-white/[0.06] text-white rounded-bl-md"
                  }`}
                >
                  {(m.attachments || []).length > 0 && (
                    <div className={`grid gap-1.5 mb-1.5 ${m.attachments.length > 1 ? "grid-cols-2" : "grid-cols-1"}`} data-testid={`dm-attachments-${m.message_id}`}>
                      {m.attachments.map((a) => (
                        <div key={a.attachment_id} className="rounded-xl overflow-hidden bg-black/30 max-w-full">
                          {a.kind === "image" ? (
                            <img
                              src={authUrl(a.url)}
                              alt="attachment"
                              className="w-full max-h-64 object-cover"
                              data-testid={`dm-attachment-image-${a.attachment_id}`}
                            />
                          ) : (
                            <video
                              src={authUrl(a.url)}
                              controls
                              preload="metadata"
                              className="w-full max-h-72 rounded-xl"
                              data-testid={`dm-attachment-video-${a.attachment_id}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                  <p className="text-[10px] opacity-60 mt-0.5">{relTime(m.created_at)}</p>
                </div>
                {mine ? (
                  <button
                    onClick={() => setDeletingMsg(m)}
                    aria-label="Delete message"
                    className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-zinc-900/90 border border-white/15 text-zinc-400 hover:text-red-400 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`dm-msg-delete-${m.message_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                ) : (
                  <button
                    onClick={() => setReportTarget(m)}
                    aria-label="Report message"
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-zinc-900/90 border border-white/15 text-zinc-400 hover:text-amber-400 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`dm-msg-report-${m.message_id}`}
                  >
                    <Flag className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {pendingAttachments.length > 0 && (
        <div className="px-4 pt-2 pb-1 border-t border-white/10 flex gap-2 overflow-x-auto no-scrollbar" data-testid="dm-pending-attachments">
          {pendingAttachments.map((a) => (
            <div key={a.local_id} className="relative w-20 h-20 rounded-xl bg-white/5 border border-white/10 grid place-items-center overflow-hidden shrink-0" data-testid={`dm-pending-${a.local_id}`}>
              {a.uploading ? (
                <Loader2 className="w-4 h-4 animate-spin text-zinc-300" />
              ) : a.error ? (
                <div className="p-1 text-center text-[10px] text-rose-300">
                  <AlertTriangle className="w-4 h-4 mx-auto" />
                  Failed
                </div>
              ) : a.kind === "image" ? (
                <img src={authUrl(a.url)} alt="" className="w-full h-full object-cover" />
              ) : (
                <Video className="w-6 h-6 text-cyan-300" />
              )}
              <button
                onClick={() => removePending(a.local_id)}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 grid place-items-center text-white"
                aria-label="Remove"
                data-testid={`dm-pending-remove-${a.local_id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={send} className="px-4 py-3 border-t border-white/10 flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/heic,image/heif,video/mp4,video/webm,video/quicktime"
          multiple
          onChange={onFilesSelected}
          className="hidden"
          data-testid="dm-attach-input"
        />
        <button
          type="button"
          onClick={pickFiles}
          disabled={sending || pendingAttachments.length >= 6}
          aria-label="Attach photo or video"
          className="h-11 w-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 grid place-items-center disabled:opacity-40"
          data-testid="dm-attach-btn"
        >
          <Paperclip className="w-4 h-4 text-zinc-300" />
        </button>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 h-11 px-4 rounded-full bg-white/5 border border-white/10 text-white text-sm placeholder:text-zinc-500 outline-none focus:border-pink-400"
          data-testid="dm-input"
        />
        <button
          type="submit"
          disabled={sending || (!text.trim() && pendingAttachments.filter((a) => a.attachment_id && !a.uploading).length === 0)}
          className="h-11 w-11 rounded-full bg-pink-500 hover:bg-pink-400 grid place-items-center disabled:opacity-40 glow-pink"
          aria-label="Send"
          data-testid="dm-send-btn"
        >
          <Send className="w-4 h-4 text-white" />
        </button>
      </form>

      <AlertDialog open={!!reportTarget} onOpenChange={(o) => !o && setReportTarget(null)}>
        <AlertDialogContent data-testid="dm-msg-report-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Report this message?</AlertDialogTitle>
            <AlertDialogDescription>
              Hidden Gem moderators will review it. Send only if it violates our community rules.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dm-msg-report-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={reportMsg}
              disabled={reporting}
              className="bg-amber-500 hover:bg-amber-400 text-black"
              data-testid="dm-msg-report-confirm"
            >
              Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deletingMsg} onOpenChange={(o) => !o && setDeletingMsg(null)}>
        <AlertDialogContent data-testid="dm-msg-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              The message will be moved to your Trash bin for 24 hours.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dm-msg-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMsg}
              className="bg-red-500 hover:bg-red-400"
              data-testid="dm-msg-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConversations(data);
      return data;
    } catch { return []; }
    finally { setLoading(false); }
  }, []);

  // Initial load + handle /messages/:vendorId deep link
  useEffect(() => {
    (async () => {
      let list = await reload();
      if (vendorId) {
        // First check if a conversation already exists locally (works for both buyer and vendor sides)
        let existing = list.find((x) => x.other_user_id === vendorId);
        if (existing) {
          setActive(existing);
          return;
        }
        // Otherwise, only buyers should be able to start a new conversation with a vendor.
        // The route param :vendorId is literally a vendor's user_id in that case.
        if (user && !user.is_vendor) {
          try {
            const { data } = await api.post("/messages/conversations", { vendor_id: vendorId });
            list = await reload();
            const c = list.find((x) => x.conversation_id === data.conversation_id) || data;
            setActive(c);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't open conversation");
            navigate("/messages", { replace: true });
          }
        } else {
          // Vendor visited a URL with someone not in their inbox — quietly drop the param
          navigate("/messages", { replace: true });
        }
      }
    })();
    // eslint-disable-next-line
  }, [vendorId, user?.user_id]);

  // Poll the conversation list every 6s for unread bumps
  useEffect(() => {
    const i = setInterval(reload, 6000);
    return () => clearInterval(i);
  }, [reload]);

  const openConvo = (c) => {
    setActive(c);
    navigate(`/messages/${c.other_user_id}`, { replace: true });
  };

  const closeThread = () => {
    setActive(null);
    navigate("/messages", { replace: true });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/messages/conversations/${deleteTarget.conversation_id}`);
      toast.success("Conversation moved to Trash");
      setConversations((cs) => cs.filter((c) => c.conversation_id !== deleteTarget.conversation_id));
      if (active?.conversation_id === deleteTarget.conversation_id) {
        closeThread();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto h-[calc(100dvh-9rem)] flex flex-col md:flex-row md:border md:border-white/10 md:rounded-3xl md:overflow-hidden" data-testid="messages-page">
      {/* List — hidden on mobile when a thread is active */}
      <aside className={`${active ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 bg-zinc-950 md:border-r md:border-white/10`}>
        <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-pink-400" />
            <h1 className="text-xl font-black tracking-tighter">Messages</h1>
          </div>
          <button
            onClick={() => navigate("/profile/trash")}
            className="text-[10px] uppercase tracking-widest font-black text-zinc-400 hover:text-pink-300 inline-flex items-center gap-1"
            data-testid="messages-trash-link"
          >
            <Trash2 className="w-3 h-3" /> Trash
          </button>
        </div>
        {loading ? (
          <div className="grid place-items-center flex-1 text-zinc-500 text-sm">Loading…</div>
        ) : (
          <ConversationList
            conversations={conversations}
            activeId={active?.conversation_id}
            onOpen={openConvo}
            onAskDelete={setDeleteTarget}
          />
        )}
      </aside>

      {/* Thread — hidden on mobile when no active */}
      <section className={`${active ? "flex" : "hidden md:flex"} flex-col flex-1`}>
        <Thread
          conversation={active}
          currentUserId={user.user_id}
          onBack={closeThread}
          onAfterSend={reload}
        />
      </section>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="conversation-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              It will disappear from your inbox and land in your Trash bin for 24 hours.
              The other person will still see the conversation on their side.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="conversation-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-400"
              data-testid="conversation-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
